import React, { useCallback, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import {
	DatePickerAtom,
	ModalAtom,
	TextFieldAtom,
} from "../../../components/atoms/atom";

const FORM_FIELDS = [
	{
		name: "name",
		type: "text",
		maxLength: 50,
		required: true,
		errorKey: "error-project-name-required",
	},
	{
		name: "projectKey",
		type: "text",
		maxLength: 12,
		required: true,
		errorKey: "error-project-key-required",
	},
	{
		name: "owner",
		type: "text",
		maxLength: 255,
		required: true,
		errorKey: "error-owner-required",
	},
	{
		name: "address",
		type: "text",
		maxLength: 128,
		required: true,
		errorKey: "error-address-required",
	},
	{
		name: "projectVolume",
		type: "number",
		min: 0,
		required: true,
		errorKey: "error-project-volume-required",
	},
	{
		name: "startDate",
		type: "date",
		required: true,
		errorKey: "error-start-date-required",
	},
];

const useFormField = (initialValue = "", validator) => {
	const [field, setField] = useState({ value: initialValue, error: "" });

	const setValue = useCallback(
		(value) => {
			setField({ value, error: validator ? validator(value) : "" });
		},
		[validator],
	);

	const setError = useCallback((error) => {
		setField((prev) => ({ ...prev, error }));
	}, []);

	return [field, setValue, setError];
};

function AddProjectModal({ show, onClose, onSubmit, mode, projectInfo = {} }) {
	const { t } = useTranslation();

	// Initialize form fields at component level
	const nameField = useFormField(projectInfo?.name ?? "", (value) =>
		!value ? "error-project-name-required" : "",
	);
	const ownerField = useFormField(projectInfo?.owner ?? "", (value) =>
		!value ? "error-owner-required" : "",
	);
	const addressField = useFormField(projectInfo?.address ?? "", (value) =>
		!value ? "error-address-required" : "",
	);
	const projectKeyField = useFormField(
		projectInfo?.projectKey ?? "",
		(value) => (!value ? "error-project-key-required" : ""),
	);
	const projectVolumeField = useFormField(
		projectInfo?.projectVolume ?? "",
		(value) =>
			!value
				? "error-project-volume-required"
				: isNaN(value)
					? "error-invalid-number"
					: "",
	);
	const startDateField = useFormField(
		projectInfo?.startDate ? new Date(projectInfo.startDate) : null,
		(value) => (!value ? "error-start-date-required" : ""),
	);

	// Create form fields object
	const formFields = React.useMemo(
		() => ({
			name: nameField,
			owner: ownerField,
			address: addressField,
			projectKey: projectKeyField,
			projectVolume: projectVolumeField,
			startDate: startDateField,
		}),
		[
			nameField,
			ownerField,
			addressField,
			projectKeyField,
			projectVolumeField,
			startDateField,
		],
	);

	const validateForm = useCallback(() => {
		let isValid = true;

		// Validate each field
		Object.values(formFields).forEach(([fieldState]) => {
			if (!fieldState.value) {
				isValid = false;
			}
		});

		// Set errors if invalid
		if (!isValid) {
			Object.entries(formFields).forEach(
				([fieldName, [_, __, setError]]) => {
					if (!formFields[fieldName][0].value) {
						const field = FORM_FIELDS.find(
							(f) => f.name === fieldName,
						);
						setError(field?.errorKey || "error-required");
					}
				},
			);
		}

		return isValid;
	}, [formFields]);

	const handleSubmit = useCallback(() => {
		if (!validateForm()) {
			return;
		}

		const formData = Object.entries(formFields).reduce(
			(acc, [key, [field]]) => {
				let value = field.value;
				if (key === "projectVolume") value = parseInt(value);
				if (key === "startDate" && value) value = new Date(value).toISOString();
				acc[key] = value;
				return acc;
			},
			{},
		);

		onSubmit({ ...projectInfo, ...formData });
	}, [formFields, projectInfo, onSubmit, validateForm]);

	return (
		<ModalAtom
			title={t(
				mode === "add"
					? "projects.add-project"
					: "projects.edit-project",
			)}
			show={show}
			handleClose={onClose}
			handleSubmit={handleSubmit}
			body={
				<Container>
					{/* Group fields into pairs for 2-column layout */}
					{chunk(FORM_FIELDS, 2).map((fieldPair, index) => (
						<Row className="mb-3" key={index}>
							{fieldPair.map((field) => (
								<Col key={field.name}>
									{field.type === "date" ? (
										<DatePickerAtom
											label={t(`projects.${field.name}`)}
											placeholder={t(
												`projects.${field.name}`,
											)}
											required={field.required}
											value={formFields[field.name][0].value}
											error={
												formFields[field.name][0].error
													? `projects.${formFields[field.name][0].error}`
													: ""
											}
											onChange={(date) =>
												formFields[field.name][1](date)
											}
										/>
									) : (
										<TextFieldAtom
											type={field.type}
											label={t(`projects.${field.name}`)}
											placeholder={t(
												`projects.${field.name}`,
											)}
											required={field.required}
											maxLength={field.maxLength}
											min={field.min}
											value={formFields[field.name][0].value}
											error={
												formFields[field.name][0].error
													? t(
														`projects.${formFields[field.name][0].error}`,
													)
													: ""
											}
											onChange={(e) =>
												formFields[field.name][1](
													e.target.value,
												)
											}
											suffix={field.type === "number" ? "€" : undefined}
											className={field.type === "number" ? "text-end" : undefined}
										/>
									)}
								</Col>
							))}
						</Row>
					))}
				</Container>
			}
		/>
	);
}

// Add helper function at the top of the file with other utilities
const chunk = (array, size) => {
	const chunked = [];
	for (let i = 0; i < array.length; i += size) {
		chunked.push(array.slice(i, i + size));
	}
	return chunked;
};

export default AddProjectModal;
